import React, { useEffect, useState, useContext } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

import { toast, canAccess as canAccessUtil } from 'utils/helpers';
import { DataTable, LicenceRequestForm } from 'components';
import { 
  LinearProgress,
  Button,
  Typography 
} from '@material-ui/core';
import { makeStyles } from "@material-ui/core/styles";
import api from 'utils/api';
import WorkReportPrintModal from './WorkReportPrintModal';
import { EstimateContext } from '../EstimateObjectForm/EstimateObjectForm';
import _ from 'lodash';

const useStyles = makeStyles(theme => ({
  root: {
    flexGrow: 1
  },
  margin: {
    margin: theme.spacing(1)
  },
  progressLabel: {
    position: "absolute",
    width: "100%",
    height: "100%",
    zIndex: 1,
    maxHeight: "25px", // borderlinearprogress root.height
    textAlign: "left",
    display: "flex",
    alignItems: "center",
    "& span": {
      width: "100%"
    }
  }
  ,
  progressContainer: {
    position: 'relative'
  },
  progressMarker: {
    position: 'absolute',
    top: -30,
    zIndex: 3,
    background: theme.palette.background.paper || '#fff',
    padding: '2px 6px',
    borderRadius: 4,
    // boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
    fontSize: '12px',
    whiteSpace: 'nowrap'
  },
  progressMarkerRight: {
    position: 'absolute',
    top: -30,
    right: 0,
    transform: 'translateX(0)',
    zIndex: 2,
    background: theme.palette.background.paper || '#fff',
    padding: '2px 6px',
    borderRadius: 4,
    fontSize: '12px',
    whiteSpace: 'nowrap'
  }
}));

const SERVICE_CODE = 'workReports';
const SERVICE_CODE_DB = 'SAM';
const SERVICE_RULE = 'services.work-reports';

const WorkReportList = ({ projectId, objectId, onCreateWorkReport, hasQuantity, contractors }) => {
  const classes = useStyles();
  const history = useHistory();
  const { t } = useTranslation();
  const estimateContext = useContext(EstimateContext);
  let print = estimateContext?.print;
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [canAccessService, setCanAccessService] = useState(false);
  const [showPrintModal, setShowPrintModal] = useState(false);

  const fetchRecords = async (projectId, objectId) => {
    setLoading(true);

    try {
      const { data } = await api.get(`/work-reports`, { params: { objectId } });
      setRecords(data);
    } catch (err) {
      console.error(err);
      toast(err.message, 'error');
    }

    setLoading(false);
  };

  const prepareTableColumns = () => {
    const columns = [
      'ID',
      'LINK',
      t('Periodas'),
      t('Aktuojama suma, EUR be PVM'),
      t('Likutis, EUR be PVM'),
      t('Rangovas'),
      t('Darbų vadovas'),
      t('Sukūrė'),
      t('Sukurta'),
    ];

    return columns;
  }

  const prepareTableData = (records) => {
    if (!records.length) return [];
    const data = records.map(record => {
      let row = [];

      row.push(record._id);
      row.push(`/projects/${projectId}/objects/${objectId}/workreports/${record._id}`);
      row.push(record.period);
      row.push(<div style={{ textAlign: 'center' }}>{record?.totals?.amountReported.toFixed(2)}</div>);
      row.push(<div style={{ textAlign: 'center' }}>{record?.totals?.amountBefore.toFixed(2)}</div>);
      row.push(record?.contractor || '');
      row.push(record.manager ? record.manager : t('Vartotojas nerastas'));
      row.push(record.createdBy ? record.createdBy.user?.fullName : t('Vartotojas nerastas'));
      row.push(record.createdAt ? moment(record.createdAt).utc().format('YYYY-MM-DD') : null);
      
      return row;
    });

    return data;
  };

  const checkLicence = async () => {
    try {
      const response = await api.post(`/licences/licence-valid`, { service: SERVICE_CODE_DB, rule: SERVICE_RULE });
      setCanAccessService(response.data.validLicence);
      return response.data.validLicence;
    } catch (err) {
      console.error(err);
      toast(t('Klaida įkeliant konfigūraciją'), 'error');
    }
  }

  useEffect(() => {
    checkLicence().then(canAccess => {
      if (canAccess) fetchRecords(projectId, objectId);
    });
  }, []);

  const renderLicenceRequest = () => {

    return (
      <LicenceRequestForm
        serviceCode={SERVICE_CODE}
      />
    )
  }
 
  const columns = prepareTableColumns();
  const data = prepareTableData(records);
  const mostRecentRecord = _.reduce(records, (mostRecent, record) => 
    record?.createdAt > mostRecent?.createdAt 
      ? record 
      : mostRecent
  );

  const progress = (() => {
    const totalAmount = mostRecentRecord?.totals.amount || 0;
    const totalReported = records.reduce((sum, r) => sum + (r?.totals?.amountReported || 0), 0);
    const percent = totalAmount > 0 ? Math.min(100, Math.max(0, (totalReported / totalAmount) * 100)) : 0;
    return { percent, totalAmount, totalReported };
  })();
  
  return (
    <>
      {canAccessService ? (
        <div>
          <div style={{ display: 'flex', justifyContent: 'end', paddingBottom: 0 }}>
            <Button variant="outlined" color="primary" onClick={() => setShowPrintModal(true)} style={{ display: 'block', textAlign: 'center' }}>
              <Typography variant="button" color="primary" style={{ fontSize: 11 }}>{t("Rangovo aktai")}</Typography>
              <div style={{ height: 0, marginTop: -5 }} />
              <Typography variant="button" color="primary" style={{ fontSize: 10, marginTop: -5 }}>{`${t("Forma")} 3`}</Typography>
            </Button>
          </div>
          <div className={classes.progressContainer} style={{ padding: 5, marginTop: 40, marginBottom: 10 }}>
            {progress?.percent > 85 ? (
              <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-start', position: 'relative', gap: 8 }}>
                <div className={classes.progressMarker} style={{ position: 'static', right: 'auto', left: 'auto', transform: 'none' }}>
                  <div style={{ textAlign: 'left', lineHeight: 1 }}>
                    <div style={{ fontSize: 12, opacity: 0.9 }}>{t('Aktuota, EUR be PVM')}:</div>
                    <div style={{ fontWeight: 600 }}>{progress?.totalReported?.toFixed(2)} EUR</div>
                  </div>
                </div>
                <div className={classes.progressMarkerRight} style={{ position: 'static', right: 'auto', left: 'auto', transform: 'none' }}>
                  <div style={{ textAlign: 'right', lineHeight: 1 }}>
                    <div style={{ fontSize: 12, opacity: 0.9 }}>{t('Iš viso, EUR be PVM')}:</div>
                    <div style={{ fontWeight: 600 }}>{progress?.totalAmount?.toFixed(2)} EUR</div>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div className={classes.progressMarker} style={{ left: `${progress?.percent}%`, transform: progress?.percent > 95 ? `translateX(-100%)` : progress?.percent > 8 ? `translateX(-50%)` : null }}>
                  <div style={{ textAlign: 'left', lineHeight: 1 }}>
                    <div style={{ fontSize: 12, opacity: 0.9 }}>{t('Aktuota, EUR be PVM')}:</div>
                    <div style={{ fontWeight: 600 }}>{progress?.totalReported?.toFixed(2)} EUR</div>
                  </div>
                </div>
                <div className={classes.progressMarkerRight}>
                  <div style={{ textAlign: 'right', lineHeight: 1 }}>
                    <div style={{ fontSize: 12, opacity: 0.9 }}>{t('Iš viso, EUR be PVM')}:</div>
                    <div style={{ fontWeight: 600 }}>{progress?.totalAmount?.toFixed(2)} EUR</div>
                  </div>
                </div>
              </>
            )}
            <LinearProgress variant="determinate" value={progress?.percent} style={{ padding: 5 }} />
          </div>
          <DataTable 
            columns={columns}
            data={data}
            createHandler={() => onCreateWorkReport()}
            createButtonDisabled={!hasQuantity}
            tableHeight="55vh"
            style={{ marginTop: 20 }}
          />
        </div>
      ) : renderLicenceRequest() }
      {showPrintModal && (
        <WorkReportPrintModal
          objectId={objectId}
          visible={showPrintModal}
          onClose={() => setShowPrintModal(false)}
          contractors={contractors}
          print={print}
        />
      )}
    </>
  )
}

export default WorkReportList;